import { useState, useEffect } from 'react';

/**
 * Detects mobile virtual keyboard height using VisualViewport API.
 * Sets a CSS variable --keyboard-h on <html> so any CSS can react.
 *
 * Returns { keyboardHeight, isKeyboardOpen } for JS-level usage.
 *
 * Fallback: on browsers where VisualViewport doesn't fire resize events on
 * keyboard open (e.g. Huawei Browser / older WebViews), we infer an
 * approximate keyboard height from `focusin`/`focusout` on editable elements.
 */
const isEditableElement = (el: EventTarget | null): el is HTMLElement => {
  if (!el || !(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  if (tag === 'TEXTAREA') return true;
  if (tag === 'INPUT') {
    const type = (el as HTMLInputElement).type.toLowerCase();
    // Non-text inputs (button, submit, checkbox, etc.) never open a keyboard.
    const nonText = new Set(['button', 'submit', 'reset', 'checkbox', 'radio', 'file', 'range', 'color', 'image', 'hidden']);
    return !nonText.has(type);
  }
  return false;
};

export function useKeyboardHeight() {
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  useEffect(() => {
    const vv = window.visualViewport;

    // Track whether visualViewport has reported any keyboard activity recently.
    // If it has, we trust its reading over the focus fallback.
    let lastVvKeyboardObservedAt = 0;
    let fallbackHeight = 0;

    const writeCssVar = (effectiveKbH: number) => {
      setKeyboardHeight(effectiveKbH);
      document.documentElement.style.setProperty(
        '--keyboard-h',
        `${effectiveKbH}px`,
      );
    };

    const update = () => {
      if (!vv) return;
      // Keyboard height = full window height minus visible viewport height
      // On iOS Safari, visualViewport.height shrinks when keyboard opens
      const kbH = Math.max(0, window.innerHeight - vv.height);

      // Only treat as keyboard if height difference is significant (> 100px)
      // to avoid false positives from address bar changes
      const effectiveKbH = kbH > 100 ? kbH : 0;

      if (effectiveKbH > 0) {
        lastVvKeyboardObservedAt = Date.now();
        fallbackHeight = 0; // vv wins
        writeCssVar(effectiveKbH);
      } else if (fallbackHeight > 0) {
        // vv says no keyboard, but fallback still thinks it's open — keep
        // fallback until focusout clears it.
        writeCssVar(fallbackHeight);
      } else {
        writeCssVar(0);
      }
    };

    // ---- Focus-based fallback (Huawei Browser & similar) -----------------
    // When an editable element gains focus and visualViewport has NOT reported
    // keyboard activity within the next ~400ms, assume the browser isn't
    // resizing the viewport and apply an estimated keyboard height so the
    // layout can still reserve space.
    const estimatedKeyboardHeight = () => {
      // Virtual keyboards take roughly 40–50% of viewport height across
      // phones and tablets (phones are short so 400px ≈ 45%; tablets are
      // tall so 45% ≈ 800px, matching the larger key size). Floor at 240px
      // for very short windows and leave the upper bound uncapped — tablets
      // genuinely need the larger footprint subtracted from the layout.
      return Math.max(240, Math.round(window.innerHeight * 0.45));
    };

    let focusTimer: number | null = null;
    const clearFocusTimer = () => {
      if (focusTimer !== null) {
        window.clearTimeout(focusTimer);
        focusTimer = null;
      }
    };

    const handleFocusIn = (e: FocusEvent) => {
      if (!isEditableElement(e.target)) return;
      // Skip the fallback for pointer+keyboard devices. Touch presence is a
      // good proxy for "this session will open a virtual keyboard"; we don't
      // gate on viewport width because tablets (large width, still touch)
      // are the exact devices that need this fallback.
      const touchCapable = typeof window !== 'undefined'
        && ('ontouchstart' in window
          || (navigator.maxTouchPoints !== undefined && navigator.maxTouchPoints > 0));
      if (!touchCapable) return;
      clearFocusTimer();
      focusTimer = window.setTimeout(() => {
        // If visualViewport has observed a keyboard recently, no fallback needed.
        if (Date.now() - lastVvKeyboardObservedAt < 500) return;
        fallbackHeight = estimatedKeyboardHeight();
        writeCssVar(fallbackHeight);
      }, 400);
    };

    const handleFocusOut = (e: FocusEvent) => {
      if (!isEditableElement(e.target)) return;
      clearFocusTimer();
      // Slight delay so focus moving between inputs doesn't thrash the layout.
      focusTimer = window.setTimeout(() => {
        // If focus is still on an editable element (moved between inputs), keep it open.
        const active = document.activeElement;
        if (isEditableElement(active)) return;
        fallbackHeight = 0;
        // Prefer vv reading if it's still reporting a keyboard.
        update();
        if (keyboardFromViewport() === 0) writeCssVar(0);
      }, 150);
    };

    const keyboardFromViewport = () => {
      if (!vv) return 0;
      const kbH = Math.max(0, window.innerHeight - vv.height);
      return kbH > 100 ? kbH : 0;
    };

    if (vv) {
      vv.addEventListener('resize', update);
      // Also listen to scroll — iOS adjusts viewport offset when scrolling with keyboard
      vv.addEventListener('scroll', update);
    }

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      if (vv) {
        vv.removeEventListener('resize', update);
        vv.removeEventListener('scroll', update);
      }
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      clearFocusTimer();
      document.documentElement.style.removeProperty('--keyboard-h');
    };
  }, []);

  return {
    keyboardHeight,
    isKeyboardOpen: keyboardHeight > 100,
  };
}
